import { initializeApp } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  browserSessionPersistence,
  Auth,
  createUserWithEmailAndPassword,
  signInWithRedirect,
  signInWithPopup,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  isSignInWithEmailLink,
  signInWithEmailLink,
  AuthProvider,
  OAuthProvider,
  GoogleAuthProvider,
  TwitterAuthProvider,
  FacebookAuthProvider,
} from 'firebase/auth';
import 'firebase/analytics';
import 'firebase/auth';
import 'firebase/database';


const firebaseConfig = {
  apiKey: import.meta.env.VITE__CVT_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE__CVT_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE__CVT_FIREBASE_DB_URL,
  projectId: import.meta.env.VITE__CVT_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE__CVT_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE__CVT_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE__CVT_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE__CVT_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);

// An admin viewing the hub as a homeowner signs in on a second, tab-scoped
// Firebase app. The default app's session is shared by every tab on the
// origin, so signing in there would log the admin out of the CRM everywhere.
const IMPERSONATION_FLAG_KEY = 'cvtImpersonation';

// Created on first use, like the default app's getAuth(): initializeAuth
// validates the config eagerly, so a module-level call breaks any import of
// this file where Firebase isn't configured (tests, CI).
let impersonationAuth: CVT.Maybe<Auth>;
const getImpersonationAuth = () => {
  if (!impersonationAuth) {
    impersonationAuth = initializeAuth(initializeApp(firebaseConfig, 'impersonation'), {
      persistence: browserSessionPersistence,
    });
  }
  return impersonationAuth;
};

const isImpersonating = () => {
  try {
    return window.sessionStorage.getItem(IMPERSONATION_FLAG_KEY) === 'true';
  } catch {
    return false;
  }
};

// Everything that reads "the" signed-in user goes through here, so an
// impersonating tab talks to the API as the homeowner and nothing else changes.
const getActiveAuth = () => (isImpersonating() ? getImpersonationAuth() : getAuth());

export const firebaseClient = {
  getAuth: getActiveAuth,
  auth: {
    createUserWithEmailAndPassword: (email: string, password: string) => createUserWithEmailAndPassword(getActiveAuth(), email, password),
    signInWithRedirect: (provider: AuthProvider) => signInWithRedirect(getActiveAuth(), provider),
    signInWithPopup: (provider: AuthProvider) => signInWithPopup(getActiveAuth(), provider),
    signInWithEmailAndPassword: (email: string, password: string) => signInWithEmailAndPassword(getActiveAuth(), email, password),
    signInWithCustomToken: (token: string) => signInWithCustomToken(getActiveAuth(), token),
    // Password-reset and hub sign-in emails are sent by our backend (see
    // authClient), so Firebase's own senders are deliberately not exposed here.
    isSignInWithEmailLink: (link: string) => isSignInWithEmailLink(getActiveAuth(), link),
    signInWithEmailLink: (email: string, link: string) => signInWithEmailLink(getActiveAuth(), email, link),
    signOut: () => signOut(getActiveAuth()),
    OAuthProvider,
    GoogleAuthProvider,
    TwitterAuthProvider,
    FacebookAuthProvider,
  },
  impersonation: {
    isActive: isImpersonating,
    start: async (token: string) => {
      await signInWithCustomToken(getImpersonationAuth(), token);
      // Flagged only once signed in, so a failed attempt leaves the tab on
      // the admin's own session.
      window.sessionStorage.setItem(IMPERSONATION_FLAG_KEY, 'true');
    },
    stop: async () => {
      window.sessionStorage.removeItem(IMPERSONATION_FLAG_KEY);
      await signOut(getImpersonationAuth());
    },
  },
};
